
import React from 'react';
import { 
  Calendar,
  Edit3,
  Plus,
  Trash2,
  RefreshCw,
  Check,
  AlertCircle,
  User
} from 'lucide-react';
import styles from '../CSS/CreateRoleForm.module.css';

const ReservationPermissionsSelector = ({ 
  selectedPermissions = [],
  onChange 
}) => {
  const reservationPermissions = [
  { 
    id: 'manageSpaces', 
    name: 'Gestionar Espacios', 
    icon: <Edit3 size={18} />, 
    description: 'Crear, editar y eliminar espacios de reserva',
    subPermissions: [
      { id: 'createSpaces', name: 'Crear espacios', icon: <Plus size={14} /> },
      { id: 'editSpaces', name: 'Editar espacios', icon: <Edit3 size={14} /> },
      { id: 'deleteSpaces', name: 'Eliminar espacios', icon: <Trash2 size={14} /> }
    ]
  },
  { 
    id: 'createPeriodicReservations', 
    name: 'Reservas Periódicas', 
    icon: <RefreshCw size={18} />, 
    description: 'Crear reservas que se repiten en múltiples días'
  },
  { 
    id: 'delegateReservations', 
    name: 'Asignar Reservas', 
    icon: <User size={18} />, 
    description: 'Asignar reservas a otros usuarios'
  }
];

  const isPermissionSelected = (permissionId) => {
    return selectedPermissions.includes(permissionId);
  };

  const togglePermission = (permissionId) => {
    const newSelected = isPermissionSelected(permissionId)
      ? selectedPermissions.filter(id => id !== permissionId)
      : [...selectedPermissions, permissionId];
    
    onChange(newSelected);
  };

  const hasSubPermissions = (permission) => {
    return permission.subPermissions && permission.subPermissions.length > 0;
  };

  const getSelectedSubPermissionsCount = (permission) => {
    if (!hasSubPermissions(permission)) return 0;
    return permission.subPermissions.filter(sub => selectedPermissions.includes(sub.id)).length;
  };

  const allSubPermissionsSelected = (permission) => {
    if (!hasSubPermissions(permission)) return false;
    return permission.subPermissions.every(sub => selectedPermissions.includes(sub.id));
  };

  const toggleAllSubPermissions = (permission) => {
    if (!hasSubPermissions(permission)) return;
    
    const allSelected = allSubPermissionsSelected(permission);
    const subPermissionIds = permission.subPermissions.map(sub => sub.id);
    
    let newSelected;
    if (allSelected) {
      newSelected = selectedPermissions.filter(id => !subPermissionIds.includes(id));
    } else {
      newSelected = [...selectedPermissions];
      subPermissionIds.forEach(id => {
        if (!newSelected.includes(id)) {
          newSelected.push(id);
        }
      });
    }
    
    onChange(newSelected);
  };

  return (
    <div className={styles.reservationPermissionsContainer}>
      <div className={styles.reservationPermissionsHeader}>
        <h4 className={styles.reservationPermissionsTitle}>
          <Calendar size={18} />
          Permisos Avanzados de Reservas
        </h4>
        <p className={styles.reservationPermissionsDescription}>
          Configura permisos específicos para la gestión de espacios y reservas
        </p>
      </div>

      <div className={styles.reservationPermissionsGrid}>
        {reservationPermissions.map(permission => {
          const isMainSelected = isPermissionSelected(permission.id);
          const hasSubPerms = hasSubPermissions(permission);
          const selectedSubCount = getSelectedSubPermissionsCount(permission);
          const allSubSelected = allSubPermissionsSelected(permission);

          return (
            <div key={permission.id} className={styles.reservationPermissionCard}>
              <div 
                className={`${styles.reservationPermissionMain} ${isMainSelected ? styles.reservationPermissionSelected : ''}`}
                onClick={() => !hasSubPerms && togglePermission(permission.id)}
              >
                <div className={styles.reservationPermissionIcon}>
                  {permission.icon}
                </div>
                
                <div className={styles.reservationPermissionInfo}>
                  <h5 className={styles.reservationPermissionName}>{permission.name}</h5>
                  <p className={styles.reservationPermissionDescription}>{permission.description}</p>
                </div>

                {!hasSubPerms && (
                  <div className={styles.reservationPermissionToggle}>
                    <label className={styles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={isMainSelected}
                        onChange={() => togglePermission(permission.id)}
                        className={styles.toggleInput}
                      />
                      <span className={`${styles.toggleSlider} ${isMainSelected ? styles.toggleActive : ''}`}>
                        <span className={styles.toggleThumb}></span>
                      </span>
                    </label>
                  </div>
                )}

                {hasSubPerms && (
                  <div className={styles.subPermissionsIndicator}>
                    <span className={styles.subPermissionsCount}>
                      {selectedSubCount}/{permission.subPermissions.length}
                    </span>
                  </div>
                )}
              </div>

              {hasSubPerms && (
                <div className={styles.subPermissionsContainer}>
                  <button
                    onClick={() => toggleAllSubPermissions(permission)}
                    className={`${styles.selectAllSubButton} ${allSubSelected ? styles.selectAllSubButtonActive : ''}`}
                  >
                    {allSubSelected ? <Check size={14} /> : <Plus size={14} />}
                    {allSubSelected ? 'Desmarcar todos' : 'Marcar todos'}
                  </button>

                  <div className={styles.subPermissionsList}>
                    {permission.subPermissions.map(subPermission => {
                      const isSubSelected = isPermissionSelected(subPermission.id);
                      
                      return (
                        <button
                          key={subPermission.id}
                          onClick={() => togglePermission(subPermission.id)}
                          className={`${styles.subPermissionItem} ${isSubSelected ? styles.subPermissionSelected : ''}`}
                        >
                          <div className={styles.subPermissionCheckbox}>
                            {isSubSelected && <Check size={12} />}
                          </div>
                          
                          <div className={styles.subPermissionIcon}>
                            {subPermission.icon}
                          </div>
                          
                          <span className={styles.subPermissionName}>
                            {subPermission.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {selectedPermissions.length === 0 && (
        <div className={styles.reservationPermissionsEmpty}>
          <AlertCircle size={16} />
          <span>No hay permisos adicionales seleccionados. Los usuarios podrán crear reservas básicas.</span>
        </div>
      )}
    </div>
  );
};

export default ReservationPermissionsSelector;